// The Space's site analytics, read in Symphony (spec 2026-10-25 §4-§5): the site strip's Visits tile (its sparkline
// and trend) and the in-app Analytics page (`/workspace/<slug>/analytics`). Pure and import-free, so the jest suite can
// load it without the app's dependency graph. Every rule here is pinned by the shared vectors in
// `native-apps/shared/site-analytics/contract.json`, which iOS `SiteAnalytics.swift` and Android `SiteAnalytics.kt` run
// too — change one, change all three.
//
// The wire shape is symphony-bff's `GetSiteAnalyticsResponse` as /api/site-analytics passes it through (JSON, camelCase;
// int64/double fields may arrive as strings). `known: false` means the read failed: the page shows its error state,
// never a row of zeros.

/* -------------------------------------------------------------------------------------------- wire shape */

export type SiteMetricKind = 'SESSIONS' | 'UNIQUE_VISITORS' | 'FORMS_SUBMITTED' | 'CLICKS_TO_CONTACT' | 'ORDERS' | 'SALES';

export interface SiteAnalyticsWireMetric {
  kind?: string;
  daily?: { date?: string; value?: number | string }[];
  /** The previous period's days, oldest first, when the server sends them (the chart's dashed line). */
  previousDaily?: { date?: string; value?: number | string }[];
  total?: number | string;
  previousTotal?: number | string;
}

export interface SiteAnalyticsWire {
  known?: boolean | null;
  metrics?: SiteAnalyticsWireMetric[];
  currency?: string;
  timeZone?: string;
  rangeDays?: number | string;
  readAt?: string;
}

export interface SiteMetricDay {
  date: string;
  value: number;
}

export interface SiteMetric {
  kind: SiteMetricKind;
  daily: SiteMetricDay[];
  previousDaily: SiteMetricDay[];
  total: number;
  previousTotal: number;
}

export type SiteAnalyticsState = 'error' | 'empty' | 'ready';

export interface SiteAnalytics {
  state: SiteAnalyticsState;
  /** The tiles, in the fixed order (`visibleMetrics`). */
  metrics: SiteMetric[];
  sells: boolean;
  currency: string;
  rangeDays: 7 | 30;
  readAt: string;
}

/* -------------------------------------------------------------------------------------------- rules */

/** The base order; ORDERS and SALES join only when the site sells. */
export const SITE_METRIC_ORDER: SiteMetricKind[] = ['SESSIONS', 'UNIQUE_VISITORS', 'FORMS_SUBMITTED', 'CLICKS_TO_CONTACT', 'ORDERS', 'SALES'];
const SALES_KINDS = new Set<SiteMetricKind>(['ORDERS', 'SALES']);
const KNOWN_KINDS = new Set<string>(SITE_METRIC_ORDER);

/** A wire number (number or numeric string); anything else, or a negative, is 0. */
export function wireNumber(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** The page's range: 7 or 30 days; anything else is 30. */
export function analyticsRange(days: unknown): 7 | 30 {
  return days === 7 ? 7 : 30;
}

export interface MetricTrend {
  direction: 'up' | 'down' | 'flat';
  percent: number;
}

/** `now` against `prev`, a whole percent rounded half away from zero; null when there is nothing to compare against. */
export function metricTrend(now: number, prev: number): MetricTrend | null {
  if (!(prev > 0)) return null;
  const raw = ((now - prev) / prev) * 100;
  const percent = Math.floor(Math.abs(raw) + 0.5);
  return { direction: percent === 0 ? 'flat' : raw > 0 ? 'up' : 'down', percent };
}

function days(list: SiteAnalyticsWireMetric['daily']): SiteMetricDay[] {
  return (list ?? []).map((d) => ({ date: typeof d?.date === 'string' ? d.date : '', value: wireNumber(d?.value) }));
}

/** The tiles to show, in the fixed order, and whether the site sells (an order or a sale in either period). */
export function visibleMetrics(metrics: SiteAnalyticsWireMetric[] | undefined): { metrics: SiteMetric[]; sells: boolean } {
  const byKind = new Map<SiteMetricKind, SiteMetric>();
  for (const m of metrics ?? []) {
    const kind = m?.kind ?? '';
    if (!KNOWN_KINDS.has(kind) || byKind.has(kind as SiteMetricKind)) continue;
    byKind.set(kind as SiteMetricKind, {
      kind: kind as SiteMetricKind,
      daily: days(m.daily),
      previousDaily: days(m.previousDaily),
      total: wireNumber(m.total),
      previousTotal: wireNumber(m.previousTotal),
    });
  }
  const sells = [...byKind.values()].some((m) => SALES_KINDS.has(m.kind) && (m.total > 0 || m.previousTotal > 0));
  const shown = SITE_METRIC_ORDER.filter((k) => byKind.has(k) && (sells || !SALES_KINDS.has(k))).map((k) => byKind.get(k) as SiteMetric);
  return { metrics: shown, sells };
}

/** error = the read failed; empty = every shown number is zero in both periods; ready otherwise. */
export function analyticsState(known: boolean | null | undefined, metrics: SiteMetric[]): SiteAnalyticsState {
  if (known !== true) return 'error';
  return metrics.every((m) => m.total === 0 && m.previousTotal === 0) ? 'empty' : 'ready';
}

/** The whole response as the page draws it. */
export function parseSiteAnalytics(wire: SiteAnalyticsWire | null | undefined, requestedDays: unknown): SiteAnalytics {
  const { metrics, sells } = visibleMetrics(wire?.metrics);
  return {
    state: analyticsState(wire?.known, metrics),
    metrics,
    sells,
    currency: typeof wire?.currency === 'string' ? wire.currency.trim().toUpperCase() : '',
    rangeDays: analyticsRange(wire?.rangeDays !== undefined ? wireNumber(wire.rangeDays) : requestedDays),
    readAt: typeof wire?.readAt === 'string' ? wire.readAt : '',
  };
}

/** Each value as a fraction of the series max (baseline 0); an all-zero series is a flat line of zeros. */
export function sparklineFractions(values: readonly unknown[]): number[] {
  const clean = values.map(wireNumber);
  const max = clean.reduce((a, b) => Math.max(a, b), 0);
  return clean.map((v) => (max > 0 ? v / max : 0));
}

/** The chart's y-axis: top = the smallest 1/2/4/6/8 × 10^k at or above max; [0, top/2, top] (or [0, top] under 2). */
export function chartTicks(max: number): number[] {
  if (!(max > 0)) return [0];
  const exp = Math.floor(Math.log10(max));
  let top = 0;
  for (const k of [exp - 1, exp, exp + 1]) {
    const base = Math.pow(10, k);
    const hit = [1, 2, 4, 6, 8].map((s) => s * base).find((c) => c >= max - 1e-9);
    if (hit !== undefined) {
      top = hit;
      break;
    }
  }
  if (top === 0) top = Math.pow(10, exp + 1);
  const round = (n: number) => Number(n.toPrecision(12));
  return top >= 2 ? [0, round(top / 2), round(top)] : [0, round(top)];
}

/** The x-axis label positions: first, middle and last, without repeats. */
export function xLabelIndices(count: number): number[] {
  if (count <= 0) return [];
  return [...new Set([0, Math.floor((count - 1) / 2), count - 1])];
}

/* -------------------------------------------------------------------------------------------- the strip's tile */

export interface StripVisitsWire {
  known?: boolean;
  sessions30d?: number | string;
  sessionsPrev30d?: number | string;
  /** The last 30 days, oldest first (the sparkline). Absent on an older server. */
  dailySessions?: (number | string)[];
}

export interface StripVisits {
  known: boolean;
  count: number;
  trend: MetricTrend | null;
  daily: number[];
}

/**
 * The strip's Visits tile, or null when there is none: `visits` present AND (known, or a Wix site — `wix`). An unknown
 * read draws "—" and still opens the Analytics page. `dailySessions` keeps its last 30 entries.
 */
export function stripVisits(visits: StripVisitsWire | null | undefined, wix: boolean): StripVisits | null {
  if (!visits) return null;
  const known = visits.known === true;
  if (!known && !wix) return null;
  if (!known) return { known: false, count: 0, trend: null, daily: [] };
  const count = Math.floor(wireNumber(visits.sessions30d));
  const daily = (Array.isArray(visits.dailySessions) ? visits.dailySessions : []).slice(-30).map((v) => Math.floor(wireNumber(v)));
  return { known, count, trend: metricTrend(count, Math.floor(wireNumber(visits.sessionsPrev30d))), daily };
}

/* -------------------------------------------------------------------------------------------- route + copy */

/** The page's route; `workspace/<slug>/analytics` deeplinks resolve to it on web. */
export function siteAnalyticsPath(slug: string): string {
  return `/workspace/${encodeURIComponent(slug)}/analytics`;
}

export const SITE_ANALYTICS_ASK_PROMPT = 'siteAnalytics.askPrompt';
export const SITE_ANALYTICS_EMPTY_ASK_PROMPT = 'siteAnalytics.empty.askPrompt';

export function metricLabelKey(kind: SiteMetricKind): string {
  return `siteAnalytics.metric.${kind}`;
}
